/** A landing page button target. Kept free of imports so it stays unit-testable, and mirrored by
 * `landingPageLinkSchema`, which is what every use site checks the two against. */
export type LandingPageLink = (
	| { type: "page"; path: string }
	| { type: "academy"; path: string }
	| { type: "custom"; url: string }
) & { openInNewTab?: boolean }

export function toLandingPageLink(url: string): LandingPageLink {
	const trimmedUrl = url.trim()
	return isWebsitePagePath(trimmedUrl) ? { type: "page", path: trimmedUrl } : { type: "custom", url: trimmedUrl }
}

/** Several values that read as a path still resolve to another origin: `//host`, `/\host`, and anything
 * with a tab or newline the parser strips before resolving. The question is answered by resolving the
 * value the way a browser would rather than by inspecting its first characters. */
export function isWebsitePagePath(url: string): boolean {
	// A url without a leading slash resolves onto this origin too, but `example.com/pricing` means the other
	// site rather than a path, so a page still has to look like one.
	return url.trim().startsWith("/") && resolveWebsitePagePath(url) !== null
}

function resolveWebsitePagePath(url: string): string | null {
	try {
		const resolved = new URL(url, websitePagePathOrigin)
		if (resolved.origin !== websitePagePathOrigin) return null

		return `${resolved.pathname}${resolved.search}${resolved.hash}`
	} catch {
		return null
	}
}

/** A host no one can register, so resolving against it can only stay on this origin for a real path. */
const websitePagePathOrigin = "https://website.invalid"

// BACKWARDS COMPAT: a button stored a bare `url` string before link targets became typed. Such a row is
// upgraded as it is parsed, so nothing has to be migrated and any writer may still submit the old shape.
// An academy url becomes a custom link, which resolves to the same href; the editor recognises it as the
// academy page it points at and stores it as one the next time the creator saves the block.
export function upgradeLegacyLandingPageButton(value: unknown): unknown {
	if (typeof value !== "object" || value === null || "link" in value) return value
	if (!("url" in value) || typeof value.url !== "string") return value

	const { url, ...button } = value
	return { ...button, link: toLandingPageLink(url) }
}

/** `academyUrl` is the academy's origin without a trailing slash, so an academy link resolves against
 * whichever domain the academy is served on today rather than the one it had when the link was picked. */
export function resolveLandingPageLinkUrl(link: LandingPageLink, academyUrl: string): string {
	switch (link.type) {
		case "page":
			return toAbsolutePath(link.path)
		case "academy":
			return `${academyUrl}${toAbsolutePath(link.path)}`
		case "custom":
			return link.url
	}
}

export function hasLandingPageLinkTarget(link: LandingPageLink): boolean {
	return (link.type === "custom" ? link.url : link.path).trim().length > 0
}

/** Rewrites every reference to a website page inside block content, so a page that is stored under a
 * different path than the one a link was written against still points at it. `resolvePath` returns the
 * path to use, or undefined to leave a reference alone. Covers both button targets and the inline links
 * a rich text field holds, which are plain hrefs rather than typed targets. */
export function rewriteLandingPagePaths(value: unknown, resolvePath: (path: string) => string | undefined): unknown {
	if (typeof value === "string") {
		// A rich text field holds a whole Lexical document in a string, so its links sit one level deeper.
		const document = parseLexicalDocument(value)
		return document === null ? value : JSON.stringify(rewriteLandingPagePaths(document, resolvePath))
	}
	if (Array.isArray(value)) return value.map(item => rewriteLandingPagePaths(item, resolvePath))
	if (typeof value !== "object" || value === null) return value

	const entry = value as Record<string, unknown>
	const rewritten = Object.fromEntries(
		Object.entries(entry).map(([key, item]) => [key, rewriteLandingPagePaths(item, resolvePath)]),
	)

	if (entry.type === "page" && typeof entry.path === "string") {
		return { ...rewritten, path: resolvePath(entry.path) ?? entry.path }
	}
	if (entry.type === "link" && typeof entry.url === "string") {
		return { ...rewritten, url: resolvePath(entry.url) ?? entry.url }
	}

	return rewritten
}

function parseLexicalDocument(value: string): unknown {
	if (!value.startsWith("{")) return null

	try {
		const parsed: unknown = JSON.parse(value)
		return typeof parsed === "object" && parsed !== null && "root" in parsed ? parsed : null
	} catch {
		return null
	}
}

/** Falls back to the home page for anything that would leave the origin, so a stored path can never send a
 * visitor elsewhere. The schema constrains a page path to a string, not to a shape. */
function toAbsolutePath(path: string): string {
	return resolveWebsitePagePath(path) ?? "/"
}
